import { z } from 'zod';
import { isCalendarDate } from '../check-ins/schema';
import { apiEnvelope } from '../envelope';

// ─── breathalyzer readings vocabulary ───────────────────────────────────────
//
// Governed vocabulary and wire shapes for the append-only
// `member_breathalyzer_readings` table: one row per breathalyzer reading a care
// person recorded for a member, or one carried over from Pelago 1.0's
// `breathalyzer_readings`. A reading is a calendar day plus a blood-alcohol
// concentration; 1.0 never stored a time or zone for it, so neither does this.
// There is no edit or delete: a correction is a new reading.

/**
 * Where a reading came from. `atlas_staff`: a care person recorded it in
 * Atlas, so the row names that staff member and carries the request's
 * idempotency key. `legacy_import`: carried over from 1.0, which never recorded
 * who entered a reading, so the row carries a `source_ref` instead.
 */
export const BREATHALYZER_READING_SOURCE = ['atlas_staff', 'legacy_import'] as const;
export const breathalyzerReadingSourceSchema = z.enum(BREATHALYZER_READING_SOURCE);
export type BreathalyzerReadingSource = z.infer<typeof breathalyzerReadingSourceSchema>;

// ─── which programmes take breathalyzer readings ────────────────────────────
//
// A server-owned key set, like `QUIT_DATE_PROGRAM_KEYS`: the record route
// refuses any other programme, and the Atlas staff view places its results
// field only on these tabs, so both read one list instead of each naming a
// programme. A test cross-checks it against the programme catalogue.

/** Programme keys whose members take breathalyzer readings. */
export const BREATHALYZER_PROGRAM_KEYS = Object.freeze(['alcohol'] as const);
export type BreathalyzerProgramKey = (typeof BREATHALYZER_PROGRAM_KEYS)[number];

/**
 * Does this programme take breathalyzer readings? FAILS CLOSED: anything that
 * is not exactly a key in the set is `false`.
 */
export function isBreathalyzerProgramKey(
  programKey: string | null | undefined,
): programKey is BreathalyzerProgramKey {
  return BREATHALYZER_PROGRAM_KEYS.some((key) => key === programKey);
}

// ─── values ─────────────────────────────────────────────────────────────────

/** Decimal places a reading carries, matching the `numeric(10,4)` column. */
export const BAC_SCALE = 4;

const BAC_PATTERN = /^\d+(\.\d{1,4})?$/;

/**
 * A BAC a care person may record: a percentage from 0 to 1 inclusive with at
 * most four decimal places, as a decimal string. It stays a string end to end
 * so no float rounding touches it. Imported 1.0 readings are not held to the
 * upper bound (1.0's own route accepted any non-negative value), which is why
 * the column's CHECK is only `>= 0`.
 */
export const bacSchema = z
  .string()
  .regex(BAC_PATTERN, 'must be a decimal with at most 4 decimal places')
  .refine((value) => compareDecimal(value, '1') <= 0, 'must be between 0 and 1');

/**
 * Rescale a non-negative decimal string to exactly four decimal places without
 * going through a JS number: `'0.02'` → `'0.0200'`, `'1'` → `'1.0000'`,
 * `'0.12345'` stays rejected by callers that validate first. Throws on a value
 * that is not a plain non-negative decimal, or that has more than four places,
 * because silently rounding a clinical value is worse than failing.
 */
export function toBacScale4(value: string): string {
  const match = /^(\d+)(?:\.(\d*))?$/.exec(value.trim());
  if (!match) throw new Error('not a non-negative decimal');
  const whole = match[1].replace(/^0+(?=\d)/, '');
  const fraction = match[2] ?? '';
  if (fraction.length > BAC_SCALE && /[1-9]/.test(fraction.slice(BAC_SCALE))) {
    throw new Error(`more than ${BAC_SCALE} decimal places`);
  }
  return `${whole}.${fraction.slice(0, BAC_SCALE).padEnd(BAC_SCALE, '0')}`;
}

/** Compare two non-negative decimal strings numerically, exactly. */
function compareDecimal(a: string, b: string): number {
  const [aw, af = ''] = a.split('.');
  const [bw, bf = ''] = b.split('.');
  const aWhole = aw.replace(/^0+(?=\d)/, '');
  const bWhole = bw.replace(/^0+(?=\d)/, '');
  if (aWhole.length !== bWhole.length) return aWhole.length < bWhole.length ? -1 : 1;
  if (aWhole !== bWhole) return aWhole < bWhole ? -1 : 1;
  const width = Math.max(af.length, bf.length);
  const aFrac = af.padEnd(width, '0');
  const bFrac = bf.padEnd(width, '0');
  if (aFrac === bFrac) return 0;
  return aFrac < bFrac ? -1 : 1;
}

/** The day a reading was taken: a real calendar day, `YYYY-MM-DD`. */
export const breathalyzerReadingDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a YYYY-MM-DD date')
  .refine(isCalendarDate, 'must be a real calendar date');

// ─── wire shapes ────────────────────────────────────────────────────────────

/**
 * Body of `POST /api/v1/internal/breathalyzer-readings?memberId=`. Strict:
 * the server rejects any key it does not know. The member comes from the query
 * string, never the body. `idempotencyKey` is minted once per dialog opening,
 * so a retried save replays instead of recording twice.
 */
export const breathalyzerReadingCreateRequestSchema = z
  .object({
    programKey: z.string().min(1),
    readingDate: breathalyzerReadingDateSchema,
    bac: bacSchema,
    idempotencyKey: z.string().uuid(),
  })
  .strict();
export type BreathalyzerReadingCreateRequest = z.infer<
  typeof breathalyzerReadingCreateRequestSchema
>;

/**
 * One reading as the staff routes return it. Not strict: Atlas parses it, and
 * a field added later must not break a bundle already in a browser.
 */
export const breathalyzerReadingSchema = z.object({
  id: z.string().uuid(),
  readingDate: z.string(),
  bac: z.string(),
  source: breathalyzerReadingSourceSchema,
  createdAt: z.string(),
});
export type BreathalyzerReading = z.infer<typeof breathalyzerReadingSchema>;

/** `GET /api/v1/internal/breathalyzer-readings` response data, newest first. */
export const breathalyzerReadingListResponseSchema = z.object({
  readings: z.array(breathalyzerReadingSchema),
});
export type BreathalyzerReadingListResponse = z.infer<typeof breathalyzerReadingListResponseSchema>;

/** Envelope of the record route: one reading. */
export const breathalyzerReadingResponseSchema = apiEnvelope(breathalyzerReadingSchema);
/** Envelope of the list route. */
export const breathalyzerReadingListEnvelopeSchema = apiEnvelope(
  breathalyzerReadingListResponseSchema,
);

// ─── refusal vocabulary ─────────────────────────────────────────────────────

/** The `error.code` values the breathalyzer staff routes may return (ADR-002). */
export const BREATHALYZER_READING_ERROR_CODES = {
  /** Malformed request, a programme without readings, or one the member is not enrolled in. */
  invalidRequest: 'invalid_request',
  /** No member with that id. */
  memberNotFound: 'member_not_found',
  /** The idempotency key was already used for this member with a different body. */
  idempotencyKeyReuse: 'idempotency_key_reuse',
} as const;
export type BreathalyzerReadingErrorCode =
  (typeof BREATHALYZER_READING_ERROR_CODES)[keyof typeof BREATHALYZER_READING_ERROR_CODES];
